import Image from 'next/image'
import {FC} from 'react'

import {Link} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {AnalyticsTrackedEvent} from '@/utils/tracking/types'

import {Text, TextFontWeight, TextVariant} from '../content/Text'

type BlogArticleCardProps = {
  title: string
  excerpt: string
  linkHref: string
  imageSrc: string
  imageAlt: string
  trackedEvent?: AnalyticsTrackedEvent
  fullWidth?: boolean
  titleMaxLines?: number
}

export const BlogArticleCard: FC<React.PropsWithChildren<BlogArticleCardProps>> = ({
  title,
  excerpt,
  linkHref,
  imageSrc,
  imageAlt,
  trackedEvent,
  fullWidth = false,
  titleMaxLines,
}) => (
  <>
    <article className={fullWidth ? 'full-width' : ''}>
      <Link href={linkHref} legacyBehavior>
        <a
          className="link"
          onClick={() =>
            trackedEvent && analytics.track(trackedEvent.name, trackedEvent.parameters)
          }
          role="link"
          tabIndex={0}
        >
          <div className="thumbnail-mask">
            <div className="thumbnail">
              <Image
                src={imageSrc}
                alt={imageAlt}
                style={{objectFit: 'cover'}}
                fill
                sizes={`${MOBILE_SCREENSIZE} 100vw, ${TABLET_SCREENSIZE} 50vw, 353px`}
              />
            </div>
          </div>
          <div className="content-wrapper">
            <div className="content">
              <div className={titleMaxLines ? 'title-clamped' : ''}>
                <Text variant={TextVariant.h5} element="h3" fontWeight={TextFontWeight.bold}>
                  {title}
                </Text>
              </div>
              <div className="excerpt">
                <Text color={theme.palette.text.light}>{excerpt}</Text>
              </div>
            </div>
          </div>
        </a>
      </Link>
    </article>

    <style jsx>{`
      article {
        height: 100%;
      }
      .link {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 16px;
      }
      .content {
        padding: ${theme.spacing[4]};
      }
      .thumbnail-mask {
        overflow: hidden;
        width: 100%;
        height: 200px;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }
      .thumbnail {
        position: relative;
        width: 100%;
        height: 100%;
      }
      .title-clamped,
      .excerpt {
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
      }
      .title-clamped {
        -webkit-line-clamp: ${titleMaxLines};
      }
      .excerpt {
        -webkit-line-clamp: 3;
        margin-top: ${theme.spacing[2.5]};
      }
      .full-width {
        border: 1px solid ${theme.palette.gray[200]};
        border-radius: 12px;
        transition: border-color ${HOVER_TRANSITION}ms ease-in-out;
      }
      .full-width:hover {
        border-color: ${theme.palette.gray[300]};
      }
      .full-width + .full-width {
        margin-top: ${theme.spacing[4]};
      }
      .full-width .link {
        display: flex;
      }
      .full-width .thumbnail-mask {
        width: 280px;
        flex: 0 0 280px;
        height: auto;
        min-height: 188px;
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
        border-top-right-radius: 0;
      }
      .full-width .content-wrapper {
        display: flex;
        align-items: center;
      }
      @media ${MOBILE_SCREENSIZE} {
        .content {
          padding: ${theme.spacing[3]};
        }
        .thumbnail-mask {
          height: 180px;
        }
        .excerpt {
          margin-top: ${theme.spacing[2]};
        }
        .full-width .link {
          display: block;
        }
        .full-width .thumbnail-mask {
          width: 100%;
          flex: 1;
          height: 130px;
          min-height: inherit;
          border-top-right-radius: 12px;
          border-bottom-left-radius: 0;
        }
        .full-width + .full-width {
          margin-top: ${theme.spacing[3]};
        }
      }
    `}</style>
  </>
)
